import React, { useCallback, useEffect } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { GatsbyImage } from 'gatsby-plugin-image'

// Embla Plugins
import Autoplay from 'embla-carousel-autoplay'
import ClassNames from 'embla-carousel-class-names'

// Embla Buttons
import { DotButton, useDotButton } from './emblaCarouselDotButton'
import { PrevButton, NextButton, usePrevNextButtons } from './emblaCarouselArrowButtons'
import { SelectedSnapDisplay, useSelectedSnapDisplay } from './emblaCarouselSelectedSnapDisplay'

const Thumb = (props) => {
  const { selected, image, onClick } = props

  return (
    <div className={'embla-thumbs__slide'.concat(selected ? ' embla-thumbs__slide--selected' : '')}>
      <button onClick={onClick} type='button' className='embla-thumbs__slide__number'>
        <GatsbyImage className='h-full w-full object-cover' image={image.gatsbyImageData} alt={image.alt || ''} />
      </button>
    </div>
  )
}

const EmblaCarousel = ({ align = 'center', arrows = true, arrowPosition = 'center', autoplay = true, children, count = false, delay = 4000, dots = true, highlight = false, loop = true, thumbs }) => {
  const options = {
    loop,
    align,
    skipSnaps: false
  }

  const autoplayOptions = {
    playOnInit: autoplay,
    delay,
    stopOnInteraction: true,
    stopOnMouseEnter: true
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [Autoplay(autoplayOptions), ClassNames()])
  const { selectedSnap, snapCount } = useSelectedSnapDisplay(emblaApi)
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true
  })

  const onThumbClick = useCallback(
    (index) => {
      if (!emblaApi || !emblaThumbsApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi, emblaThumbsApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return
    // setSelectedIndex(emblaApi.selectedScrollSnap())
    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap())
  }, [emblaApi, emblaThumbsApi]) //, setSelectedIndex

  const onNavButtonClick = useCallback((emblaApi) => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay) return
    const resetOrStop = autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop
    resetOrStop()
  }, [])

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi, onNavButtonClick)
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi, onNavButtonClick)

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className={`gatsby relative flec flex-col embla ${highlight ? 'embla-highlight' : ''}`}>
      <div className='embla__viewport' ref={emblaRef}>
        <div className='embla__container'>{children}</div>
      </div>
      <div className='embla__controls'>
        {count && <SelectedSnapDisplay selectedSnap={selectedSnap} snapCount={snapCount} />}
        {arrows && !thumbs && (
          <div className={`embla__buttons ${arrowPosition === 'center' && 'mx-auto'}`}>
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
            <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
          </div>
        )}
        {dots && !thumbs && (
          <div className='embla__dots'>
            {scrollSnaps.map((_, index) => (
              <DotButton key={index} onClick={() => onDotButtonClick(index)} className={'embla__dot'.concat(index === selectedIndex ? ' embla__dot--selected' : '')} />
            ))}
          </div>
        )}
        {thumbs && (
          <div className='embla-thumbs'>
            <div className='embla-thumbs__viewport' ref={emblaThumbsRef}>
              <div className='embla-thumbs__container'>
                {thumbs.map((thumb, index) => (
                  <Thumb image={thumb.image} key={index} onClick={() => onThumbClick(index)} selected={index === selectedIndex} index={index} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmblaCarousel
